import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import {
  FaTimes,
  FaCalendarPlus,
  FaCalendarCheck,
  FaClock,
  FaMapMarkerAlt,
  FaVideo,
  FaUser,
  FaUserTie,
  FaBell,
  FaSave,
} from "react-icons/fa";

const CalendarEventModal = ({ isOpen, onClose, onSaved, initialDate, eventToEdit }) => {
  const [formData, setFormData] = useState({
    title: "",
    event_type: "Meeting",
    priority: "medium",
    start_time: "",
    end_time: "",
    is_all_day: false,
    location: "",
    meeting_link: "",
    client_name: "",
    assigned_to: "",
    reminder_minutes_before: 30,
    description: "",
  });

  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const formatForDateTimeLocal = (dateInput) => {
    if (!dateInput) return "";
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => (n < 10 ? "0" + n : n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  useEffect(() => {
    if (isOpen) {
      // Fetch customers and employees for dropdowns
      axios
        .get(`${API_BASE_URL}/customers/`)
        .then((res) => {
          if (Array.isArray(res.data)) {
            setCustomers(res.data);
          } else if (Array.isArray(res.data?.items)) {
            setCustomers(res.data.items);
          }
        })
        .catch(() => {});

      axios
        .get(`${API_BASE_URL}/employees/list`)
        .then((res) => {
          if (Array.isArray(res.data)) {
            setEmployees(res.data);
          } else if (Array.isArray(res.data?.items)) {
            setEmployees(res.data.items);
          }
        })
        .catch(() => {});

      if (eventToEdit) {
        setFormData({
          title: eventToEdit.title || "",
          event_type: eventToEdit.event_type || "Meeting",
          priority: eventToEdit.priority || "medium",
          start_time: formatForDateTimeLocal(eventToEdit.start_time),
          end_time: formatForDateTimeLocal(eventToEdit.end_time),
          is_all_day: eventToEdit.is_all_day || false,
          location: eventToEdit.location || "",
          meeting_link: eventToEdit.meeting_link || "",
          client_name: eventToEdit.client_name || "",
          assigned_to: eventToEdit.assigned_to || "",
          reminder_minutes_before: eventToEdit.reminder_minutes_before ?? 30,
          description: eventToEdit.description || "",
        });
      } else {
        const baseDate = initialDate ? new Date(initialDate) : new Date();
        const start = new Date(baseDate);
        if (!initialDate) {
          start.setMinutes(0);
          start.setSeconds(0);
        }
        const end = new Date(start);
        end.setHours(start.getHours() + 1);

        setFormData({
          title: "",
          event_type: "Meeting",
          priority: "medium",
          start_time: formatForDateTimeLocal(start),
          end_time: formatForDateTimeLocal(end),
          is_all_day: false,
          location: "",
          meeting_link: "",
          client_name: "",
          assigned_to: "",
          reminder_minutes_before: 30,
          description: "",
        });
      }
      setErrorMsg("");
    }
  }, [isOpen, eventToEdit, initialDate]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setErrorMsg("Please enter an event or meeting title.");
      return;
    }
    if (!formData.start_time) {
      setErrorMsg("Please specify a start time.");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    const payload = {
      ...formData,
      start_time: new Date(formData.start_time).toISOString(),
      end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null,
      reminder_minutes_before: parseInt(formData.reminder_minutes_before, 10) || 0,
    };

    try {
      if (eventToEdit && eventToEdit.id) {
        await axios.put(`${API_BASE_URL}/calendar/events/${eventToEdit.id}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/calendar/events`, payload);
      }
      setLoading(false);
      onSaved();
      onClose();
    } catch (err) {
      setLoading(false);
      const detail = err.response?.data?.detail;
      setErrorMsg(typeof detail === "string" ? detail : "Failed to save event. Please check details.");
    }
  };

  return (
    <div className="calendar-modal-overlay" onClick={onClose}>
      <div className="calendar-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="calendar-modal-header">
          <h3>
            {eventToEdit ? <FaCalendarCheck /> : <FaCalendarPlus />}
            {eventToEdit ? "Edit Scheduled Event / Meeting" : "Schedule New Event / Meeting"}
          </h3>
          <button className="calendar-modal-close-btn" onClick={onClose} type="button">
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="calendar-modal-body">
            {errorMsg && (
              <div style={{ backgroundColor: "#fef2f2", color: "#991b1b", padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13, border: "1px solid #fecaca" }}>
                {errorMsg}
              </div>
            )}

            <div className="calendar-form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                className="calendar-input"
                placeholder="e.g., Client Portfolio Review, Ticket Follow-up"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="calendar-form-row">
              <div className="calendar-form-group">
                <label>Event Type</label>
                <select
                  name="event_type"
                  className="calendar-select"
                  value={formData.event_type}
                  onChange={handleChange}
                >
                  <option value="Meeting">Client / Partner Meeting</option>
                  <option value="Follow-up">Client Follow-up</option>
                  <option value="Schedule">Schedule / Appointment</option>
                  <option value="Task Deadline">Important Task</option>
                  <option value="Reminder">Reminder</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="calendar-form-group">
                <label>Priority</label>
                <select
                  name="priority"
                  className="calendar-select"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div className="calendar-form-row">
              <div className="calendar-form-group">
                <label>
                  <FaClock style={{ marginRight: 6, color: "#64748b" }} />
                  Start Time *
                </label>
                <input
                  type="datetime-local"
                  name="start_time"
                  className="calendar-input"
                  value={formData.start_time}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="calendar-form-group">
                <label>
                  <FaClock style={{ marginRight: 6, color: "#64748b" }} />
                  End Time
                </label>
                <input
                  type="datetime-local"
                  name="end_time"
                  className="calendar-input"
                  value={formData.end_time}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="calendar-form-group">
              <label className="calendar-checkbox-row">
                <input
                  type="checkbox"
                  name="is_all_day"
                  checked={formData.is_all_day}
                  onChange={handleChange}
                />
                All-Day Event
              </label>
            </div>

            <div className="calendar-form-row">
              <div className="calendar-form-group">
                <label>
                  <FaMapMarkerAlt style={{ marginRight: 6, color: "#64748b" }} />
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  className="calendar-input"
                  placeholder="e.g. Conference Room B, Dubai Office"
                  value={formData.location}
                  onChange={handleChange}
                />
              </div>

              <div className="calendar-form-group">
                <label>
                  <FaVideo style={{ marginRight: 6, color: "#64748b" }} />
                  Virtual Meeting Link
                </label>
                <input
                  type="url"
                  name="meeting_link"
                  className="calendar-input"
                  placeholder="e.g. https://meet.google.com/xyz"
                  value={formData.meeting_link}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="calendar-form-row">
              <div className="calendar-form-group">
                <label>
                  <FaUser style={{ marginRight: 6, color: "#64748b" }} />
                  Client / Customer Name
                </label>
                <input
                  list="customers-datalist"
                  name="client_name"
                  className="calendar-input"
                  placeholder="Type or select customer"
                  value={formData.client_name}
                  onChange={handleChange}
                />
                <datalist id="customers-datalist">
                  {customers.map((c) => (
                    <option key={c.id} value={c.name || c.full_name || c.company_name} />
                  ))}
                </datalist>
              </div>

              <div className="calendar-form-group">
                <label>
                  <FaUserTie style={{ marginRight: 6, color: "#64748b" }} />
                  Assigned Team Member
                </label>
                <input
                  list="employees-datalist"
                  name="assigned_to"
                  className="calendar-input"
                  placeholder="Type or select employee"
                  value={formData.assigned_to}
                  onChange={handleChange}
                />
                <datalist id="employees-datalist">
                  {employees.map((e) => (
                    <option key={e.id} value={e.name || `${e.first_name || ""} ${e.last_name || ""}`.trim()} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="calendar-form-group">
              <label>
                <FaBell style={{ marginRight: 6, color: "#64748b" }} />
                Reminder Alert Notice
              </label>
              <select
                name="reminder_minutes_before"
                className="calendar-select"
                value={formData.reminder_minutes_before}
                onChange={handleChange}
              >
                <option value={0}>At time of event</option>
                <option value={15}>15 minutes before</option>
                <option value={30}>30 minutes before</option>
                <option value={60}>1 hour before</option>
                <option value={120}>2 hours before</option>
                <option value={1440}>1 day before (24 hours)</option>
                <option value={2880}>2 days before (48 hours)</option>
              </select>
            </div>

            <div className="calendar-form-group">
              <label>Notes / Description</label>
              <textarea
                name="description"
                className="calendar-textarea"
                placeholder="Meeting agenda, flight details, or task instructions..."
                value={formData.description}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="calendar-modal-footer">
            <button
              type="button"
              className="calendar-secondary-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="calendar-primary-btn"
              disabled={loading}
            >
              <FaSave />
              {loading ? "Saving..." : eventToEdit ? "Update Event" : "Save & Schedule"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CalendarEventModal;
